import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box,
  Flex,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import { ApolloClient } from '@apollo/client';

import { Section } from './Section';
import { AssignCodersModal } from './AssignCodersModal';
import { MANAGER_HOME_QUERY, ARCHIVE_PROJECT_MUTATION } from './managerHome.graphql';
import { useGroupFilterPersistence } from './useGroupFilterPersistence';
import { applyGroupFilter, applyNameFilter, sortProjects, getAuthHeader } from './utils';
import {
  ManagerHomeGroup,
  ManagerHomeProject,
  ManagerHomeSection,
  SECTION_ORDER,
} from './types';

interface ManagerHomeQueryResult {
  managerHome: {
    manager: { userId: string; userName: string };
    groups: ManagerHomeGroup[];
    projects: ManagerHomeProject[];
  };
}

interface ArchiveToastContentProps {
  projectName: string;
  onClose: () => void;
  onUndo: () => Promise<void>;
}

const ArchiveToastContent: React.FC<ArchiveToastContentProps> = ({ projectName, onClose, onUndo }) => {
  const toast = useToast();

  const handleUndo = async () => {
    onClose();
    try {
      await onUndo();
    } catch (err) {
      console.error('Failed to restore project', err);
      toast({ title: 'Failed to restore project', status: 'error', duration: 3000 });
    }
  };

  return (
    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p="3" boxShadow="md">
      <Flex align="center" justify="space-between" gap="4">
        <Text fontSize="sm">"{projectName}" archived</Text>
        <Text
          as="button"
          fontSize="sm"
          fontWeight={600}
          color="blue.600"
          cursor="pointer"
          onClick={handleUndo}
        >
          Undo
        </Text>
      </Flex>
    </Box>
  );
};

const PAGE_EMPTY_COPY =
  "You don't have any projects yet. Projects appear here when you're added to a group that has active CQA projects. Talk to your admin if you expect to see projects here.";

export interface ManagerHomePageProps {
  apolloClient: ApolloClient<any>;
  onProjectSelected: (projectId: string, status?: string) => void;
  searchTerm: string;
}

export const ManagerHomePage: React.FC<ManagerHomePageProps> = ({
  apolloClient,
  onProjectSelected,
  searchTerm,
}) => {
  const { data, loading, error, refetch } = useQuery<ManagerHomeQueryResult>(MANAGER_HOME_QUERY, {
    client: apolloClient,
    fetchPolicy: 'network-only',
  });

  const [assignmentProject, setAssignmentProject] = useState<ManagerHomeProject | null>(null);
  const [rubricNameMap, setRubricNameMap] = useState<Map<number, string>>(new Map());
  const toast = useToast();

  const groups = data?.managerHome?.groups ?? [];
  const rawProjects = data?.managerHome?.projects ?? [];
  const availableGroupIds = useMemo(() => groups.map((g) => g.id), [groups]);
  const { selectedGroupIds, toggleGroup } = useGroupFilterPersistence(availableGroupIds);

  // Fetch all rubric names once after the main query resolves
  useEffect(() => {
    if (rawProjects.length === 0) return;
    const uniqueRubricIds = [
      ...new Set(rawProjects.map((p) => p.rubricId).filter((id): id is number => id != null)),
    ];
    if (uniqueRubricIds.length === 0) return;

    fetch('/api/cqa-service/v1/rubrics', {
      headers: {
        Accept: 'application/json',
        Authorization: getAuthHeader(),
      },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((rubrics: Array<{ id: number; name: string }>) => {
        const idSet = new Set(uniqueRubricIds);
        const map = new Map<number, string>();
        for (const r of rubrics) {
          if (idSet.has(r.id)) map.set(r.id, r.name);
        }
        setRubricNameMap(map);
      })
      .catch((err) => console.error('Failed to load rubric names', err));
  }, [rawProjects]);

  // Merge rubricName into project objects
  const projects = useMemo(
    () =>
      rawProjects.map((p) => ({
        ...p,
        rubricName: p.rubricId != null ? (rubricNameMap.get(p.rubricId) ?? null) : null,
      })),
    [rawProjects, rubricNameMap],
  );

  const handleRowClick = (project: ManagerHomeProject) => {
    if (project.section === 'coder_complete') {
      onProjectSelected(project.id, 'CQA_COMPLETE');
    } else if (project.section === 'in_flight') {
      onProjectSelected(project.id, 'IN_CQA');
    } else {
      onProjectSelected(project.id);
    }
  };

  const handleReassign = useCallback((project: ManagerHomeProject) => {
    setAssignmentProject(project);
  }, []);

  const handleArchive = useCallback(
    async (project: ManagerHomeProject) => {
      try {
        await apolloClient.mutate({
          mutation: ARCHIVE_PROJECT_MUTATION,
          variables: { id: project.id, qarule: { availability: 'UNAVAILABLE' } },
        });
        refetch();
        toast({
          title: `"${project.name}" archived`,
          status: 'success',
          duration: 6000,
          isClosable: true,
          render: ({ onClose }) => (
            <ArchiveToastContent
              projectName={project.name}
              onClose={onClose}
              onUndo={async () => {
                await apolloClient.mutate({
                  mutation: ARCHIVE_PROJECT_MUTATION,
                  variables: { id: project.id, qarule: { availability: 'AVAILABLE' } },
                });
                refetch();
              }}
            />
          ),
        });
      } catch (err) {
        console.error('Failed to archive project', err);
        toast({ title: 'Failed to archive project', status: 'error', duration: 3000 });
      }
    },
    [apolloClient, refetch, toast],
  );

  const handleModalClose = () => {
    setAssignmentProject(null);
    refetch();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p="4rem">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p="2rem">
        <Text color="red.500">{error.message}</Text>
      </Box>
    );
  }

  if (projects.length === 0) {
    return (
      <Box data-testid="manager-home-empty" p="4rem" textAlign="center">
        <Text>{PAGE_EMPTY_COPY}</Text>
      </Box>
    );
  }

  const filtered = applyNameFilter(applyGroupFilter(projects, selectedGroupIds), searchTerm);
  const bySection = new Map<ManagerHomeSection, ManagerHomeProject[]>();
  for (const section of SECTION_ORDER) bySection.set(section, []);
  for (const project of filtered) bySection.get(project.section)?.push(project);

  return (
    <>
      <Box p="2rem" overflow="auto" height="100%">
        {/* Page header */}
        <Flex align="center" mb="1.5rem">
          <Text
            fontFamily="var(--font-heading)"
            fontSize="var(--fs-xl)"
            fontWeight={600}
            color="var(--color-text-high-emphasis)"
          >
            Project Health Dashboard
          </Text>

          <Flex align="center" gap="8px" ml="auto">
            <Text
              fontFamily="var(--font-body)"
              fontSize="0.85rem"
              color="var(--color-text-medium-emphasis)"
              fontWeight={500}
              mr="4px"
            >
              Groups:
            </Text>
            {groups.map((group) => {
              const active = selectedGroupIds.has(group.id);
              return (
                <Box
                  key={group.id}
                  as="button"
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  data-testid={`group-chip-${group.id}`}
                  px="10px"
                  py="4px"
                  borderRadius="var(--r-md)"
                  border="1px solid"
                  borderColor={active ? 'var(--color-chips-brown-500)' : 'var(--color-chips-grey-500)'}
                  bg={active ? 'var(--color-chips-brown-50)' : 'var(--color-chips-grey-50)'}
                  color={active ? 'var(--color-chips-brown-500)' : 'var(--color-chips-grey-500)'}
                  fontSize="0.78rem"
                  fontWeight={active ? 600 : 400}
                  cursor="pointer"
                  _hover={{
                    bg: 'var(--color-chips-brown-50)',
                    borderColor: 'var(--color-chips-brown-500)',
                    color: 'var(--color-chips-brown-500)',
                  }}
                >
                  {group.label}
                </Box>
              );
            })}
          </Flex>
        </Flex>

        {/* Sections */}
        {SECTION_ORDER.map((section) => (
          <Section
            key={section}
            section={section}
            projects={sortProjects(bySection.get(section) ?? [])}
            onRowClick={handleRowClick}
            onReassign={handleReassign}
            onArchive={handleArchive}
          />
        ))}
      </Box>

      {/* Assign / Re-assign Modal */}
      {assignmentProject && (
        <AssignCodersModal
          isOpen={!!assignmentProject}
          onClose={handleModalClose}
          ruleId={assignmentProject.ruleId}
          collectionId={assignmentProject.id}
          projectName={assignmentProject.name}
          groupId={assignmentProject.groupId}
          apolloClient={apolloClient}
        />
      )}
    </>
  );
};

ManagerHomePage.displayName = 'ManagerHomePage';
export default ManagerHomePage;
